import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Mail, MapPin, Building2, Wrench } from "lucide-react";
import { PendingInvitesCard } from "./admin/PendingInvitesCard";
import { CsvImportCard } from "./admin/CsvImportCard";
import { DiscordImportCard } from "./admin/DiscordImportCard";
import { SlackBridgeCard } from "./admin/SlackBridgeCard";
import { TaxonomyManager } from "./admin/TaxonomyManager";
import { UsersTable } from "./admin/UsersTable";
import { InviteModal } from "./admin/InviteModal";
import { NewUserModal } from "./admin/NewUserModal";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [taxonomies, setTaxonomies] = useState({ location: [], department: [], job: [] });
  const [openInvite, setOpenInvite] = useState(false);
  const [openUser, setOpenUser] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const loadUsers = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)), []);
  const loadInvites = useCallback(
    () => api.get("/invites").then(({ data }) => setInvites(data)).catch(() => setInvites([])),
    []
  );
  const loadTax = useCallback(async () => {
    const [loc, dept, job] = await Promise.all([
      api.get("/taxonomies", { params: { kind: "location" } }),
      api.get("/taxonomies", { params: { kind: "department" } }),
      api.get("/taxonomies", { params: { kind: "job" } }),
    ]);
    setTaxonomies({ location: loc.data, department: dept.data, job: job.data });
  }, []);

  useEffect(() => {
    loadUsers();
    loadInvites();
    loadTax();
  }, [loadUsers, loadInvites, loadTax]);

  const remove = useCallback(async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      const { data } = await api.delete(`/users/${id}`);
      loadUsers();
      toast(`User deleted`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/users/restore", { doc: data.deleted });
              toast.success("User restored");
              loadUsers();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Restore failed");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }, [loadUsers]);

  const toggleSelect = useCallback((id) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  const toggleAll = useCallback(
    () => setSelected((cur) => (cur.size === users.length ? new Set() : new Set(users.map((u) => u.id)))),
    [users]
  );

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} user${ids.length === 1 ? "" : "s"}? Undo available for 6s.`)) return;
    try {
      const { data } = await api.post("/users/bulk-delete", { ids });
      setSelected(new Set());
      loadUsers();
      const errCount = (data.errors || []).length;
      const okCount = (data.deleted || []).length;
      toast(`Deleted ${okCount} user${okCount === 1 ? "" : "s"}${errCount ? ` · ${errCount} skipped` : ""}`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            for (const doc of data.deleted) await api.post("/users/restore", { doc }).catch(() => {});
            toast.success(`Restored ${data.deleted.length}`);
            loadUsers();
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  }, [selected, loadUsers]);

  const updateRole = useCallback(async (id, role) => {
    await api.patch(`/users/${id}`, { role });
    loadUsers();
  }, [loadUsers]);

  const memoTax = useMemo(() => taxonomies, [taxonomies]);

  return (
    <div className="space-y-10" data-testid="admin-page">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 fade-in-up">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Admin</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Team Management</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setOpenInvite(true)}
            data-testid="admin-send-invite"
            className="px-4 py-2.5 bg-[color:var(--accent)] text-black rounded-lg font-bold flex items-center gap-2 hover:bg-[color:var(--accent-hover)] transition-colors"
          >
            <Mail size={16} /> Send Invite
          </button>
          <button
            onClick={() => setOpenUser(true)}
            data-testid="admin-new-user"
            className="px-4 py-2.5 border border-slate-700 hover:border-[color:var(--accent)] rounded-lg font-bold flex items-center gap-2 text-slate-200 transition-colors"
          >
            <Plus size={16} /> Add Directly
          </button>
        </div>
      </div>

      <PendingInvitesCard invites={invites} reload={loadInvites} />

      <section>
        <h2 className="text-lg font-bold mb-3 text-slate-200">Import &amp; sync</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <CsvImportCard onDone={loadUsers} />
          <DiscordImportCard onDone={loadUsers} />
        </div>
        <div className="mt-6">
          <SlackBridgeCard />
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-6">
        <TaxonomyManager kind="location" icon={<MapPin size={18} />} title="Locations" items={memoTax.location} reload={loadTax} />
        <TaxonomyManager kind="department" icon={<Building2 size={18} />} title="Departments" items={memoTax.department} reload={loadTax} />
        <TaxonomyManager kind="job" icon={<Wrench size={18} />} title="Jobs" items={memoTax.job || []} reload={loadTax} />
      </section>

      <UsersTable
        users={users}
        selected={selected}
        toggleSelect={toggleSelect}
        toggleAll={toggleAll}
        onDelete={remove}
        onRoleChange={updateRole}
        onBulkDelete={bulkDelete}
      />

      {openInvite && (
        <InviteModal
          users={users}
          taxonomies={memoTax}
          onClose={() => setOpenInvite(false)}
          onSent={() => { loadInvites(); setOpenInvite(false); }}
        />
      )}
      {openUser && (
        <NewUserModal
          users={users}
          taxonomies={memoTax}
          onClose={() => setOpenUser(false)}
          onCreated={() => { loadUsers(); setOpenUser(false); }}
        />
      )}
    </div>
  );
}
